import React, { useState, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Row, Col, Form } from 'react-bootstrap';
import { selectUser, useAppSelector } from 'slices/commonSelectors';
import FormSelectField from 'components/IAP/formFields/FormSelectField';
import FormTextField from 'components/IAP/formFields/FormTextField';
import FormTextAreaField from 'components/IAP/formFields/FormTextAreaField';
import FormDateTimeField from 'components/IAP/formFields/FormDateTimeField';
import { useResourceSites } from 'components/IAP/hooks/resourceSiteHooks';
import { useCurrentUserApprovalAuthority } from '../../../../hooks/incident/useCurrentUserApprovalAuthority';
import ApprovalAuthorityWarning from '../../../DesignSystems/ApprovalAuthorityWarning';
import CustomModal from 'components/DesignSystems/New/CustomModal';
import { ResourceInIncident } from 'components/IAP/hooks/resourceInIncidentHooks';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { useOptionalIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { CheckOutFormData } from 'components/IAP/Resources/Drawers/CheckOutDrawer';
import { useIncidentRoster } from '../../../IAP/hooks/incidentRoster/useIncidentRoster';

const checkoffUnits = [
  { id: 'supply', label: 'Supply Unit' },
  { id: 'communications', label: 'Communications Unit' },
  { id: 'time', label: 'Time Unit' },
  { id: 'documentation', label: 'Documentation Unit' },
  { id: 'demobilization', label: 'Demobilization Unit' },
];
interface CheckoffSignature {
  checked: boolean;
  name?: string;
  position?: string;
  remarks?: string;
  user_guid?: string;
  datetime?: string | null;
}
export const DemobilizedModal: React.FC<{
  resource: ResourceInIncident;
  setShowDemobilizedModal: (show: boolean) => void;
  onSave: (data: any) => void;
}> = ({ resource, setShowDemobilizedModal, onSave }) => {
  const icsFormPdf = useOptionalIcsFormPdfPreview();
  const exportAsForm = icsFormPdf?.exportAsForm;
  const hasApprovalAuthority = useCurrentUserApprovalAuthority();
  const { data: resourceSites = [] } = useResourceSites();
  const { data: rosterData } = useIncidentRoster();
  const currentUser = useAppSelector(selectUser);

  const currentMember = rosterData?.find(
    (member: any) => member.user_guid === currentUser?.user_guid
  );

  const [checkoffs, setCheckoffs] = useState<Record<string, CheckoffSignature>>(
    () => {
      // Start with all checkoffUnits initialized to defaults
      const base: Record<string, CheckoffSignature> = checkoffUnits.reduce(
        (acc, unit) => ({
          ...acc,
          [unit.id]: {
            checked: false,
            name: '',
            position: '',
            remarks: '',
            user_guid: '',
            datetime: null,
          },
        }),
        {}
      );
      // Overlay any saved values from resource.checkout_checkoffs
      if (resource?.checkout_checkoffs) {
        Object.entries(resource.checkout_checkoffs).forEach(
          ([key, value]: [string, CheckoffSignature]) => {
            if (base[key]) {
              base[key] = {
                checked: value.checked || false,
                name: value.name || '',
                position: value.position || '',
                remarks: value.remarks || '',
                user_guid: value.user_guid || '',
                datetime: value.datetime || null,
              };
            }
            // Custom signatures (not in checkoffUnits) are ignored as before
          }
        );
      }
      return base;
    }
  );
  const handleCheckoffChange = (
    unitId: string,
    field: 'checked' | 'remarks' | 'position',
    value: boolean | string
  ) => {
    setCheckoffs((prev) => {
      const current = prev[unitId];

      // If checking the box, auto-fill name and position
      if (field === 'checked' && value === true && currentMember) {
        // Only set new timestamp if this is the first time being checked
        const isFirstTimeChecking = !current.checked || !current.datetime;

        return {
          ...prev,
          [unitId]: {
            ...current,
            checked: true,
            name: currentMember.userName || currentUser.user_name || '',
            position:
              currentMember.incidentPositions
                .map((pos) => pos.positionName)
                .join(', ') || '',
            user_guid: currentUser.user_guid,
            datetime: isFirstTimeChecking
              ? new Date().toISOString()
              : current.datetime,
          },
        };
      }

      // If unchecking, only allow if current user checked it
      if (field === 'checked' && value === false) {
        if (current.user_guid && current.user_guid !== currentUser.user_guid) {
          return prev; // Don't allow unchecking
        }
        return {
          ...prev,
          [unitId]: {
            checked: false,
            name: '',
            position: '',
            remarks: '',
            user_guid: '',
            datetime: null,
          },
        };
      }

      // Handle remarks update
      if (field === 'remarks') {
        return {
          ...prev,
          [unitId]: {
            ...current,
            remarks: value as string,
          },
        };
      }

      return prev;
    });
  };

  const form = useForm<CheckOutFormData>({
    mode: 'onChange',
    defaultValues: {
      useCustomDestination:
        !!resource.destination_geolocation && !resource.destination_site_id,
      destinationSiteId: resource.destination_site_id || '',
      destinationGeolocation: resource.destination_geolocation || null,
      checkoutEtaHome: resource.checkout_eta_home || '',
      checkoutTravelMethod: resource.checkout_travel_method || '',
      checkoutContactInfo: resource.checkout_contact_info || '',
      checkoutRemarks: resource.checkout_remarks || '',
      plannedReleaseDatetime: resource.planned_release_datetime || '',
      roomOvernight: resource.room_overnight ? 'yes' : 'no',
      estimatedDepartureDatetime: resource.estimated_departure_datetime || '',
      actualReleaseDatetime: resource.actual_release_datetime || '',
      estimatedArrivalDatetime: resource.estimated_arrival_datetime || '',
      manifestRequired: resource.manifest_required ? 'yes' : 'no',
      manifestNumber: resource.manifest_number || '',
      unitAgencyNotified: resource.unit_agency_notified || '',
      unitAgencyNotifiedBool: resource.unit_agency_notified_bool ? 'yes' : 'no',
      incidentLodgingInfo: resource.incident_lodging_info || '',
    },
  });
  const { handleSubmit, watch } = form;
  const manifestRequired = watch('manifestRequired');
  const unitAgencyNotifiedBool = watch('unitAgencyNotifiedBool');

  const resourceSiteOptions = useMemo(
    () =>
      resourceSites.map((site) => ({
        value: site.id,
        label: site.name,
      })),
    [resourceSites]
  );

  const onSubmit = async (data: CheckOutFormData) => {
    const allCheckoffs = { ...checkoffs };

    const checkoffsWithDateTime = Object.entries(allCheckoffs).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: {
          ...value,
          // Preserve existing datetime, don't create new one
          datetime: value.datetime || null,
        },
      }),
      {}
    );

    const checkoutData: Partial<ResourceInIncident> = {
      release_method: 'demobilized', // Always set to demobilized
      checkout_eta_home: data.checkoutEtaHome || undefined,
      checkout_travel_method: data.checkoutTravelMethod,
      checkout_contact_info: data.checkoutContactInfo,
      checkout_remarks: data.checkoutRemarks || undefined,
      checkout_checkoffs: checkoffsWithDateTime,
      planned_release_datetime: data.plannedReleaseDatetime || undefined,
      room_overnight:
        data.roomOvernight === 'yes'
          ? true
          : data.roomOvernight === 'no'
            ? false
            : undefined,
      estimated_departure_datetime:
        data.estimatedDepartureDatetime || undefined,
      actual_release_datetime: data.actualReleaseDatetime || undefined,
      estimated_arrival_datetime: data.estimatedArrivalDatetime || undefined,
      manifest_required:
        data.manifestRequired === 'yes'
          ? true
          : data.manifestRequired === 'no'
            ? false
            : undefined,
      manifest_number:
        data.manifestRequired === 'yes' ? data.manifestNumber : undefined,
      unit_agency_notified:
        data.unitAgencyNotifiedBool === 'yes'
          ? data.unitAgencyNotified
          : undefined,
      unit_agency_notified_bool:
        data.unitAgencyNotifiedBool === 'yes'
          ? true
          : data.unitAgencyNotifiedBool === 'no'
            ? false
            : undefined,
      incident_lodging_info: data.incidentLodgingInfo || undefined,
    };

    // Handle destination based on toggle
    if (data.useCustomDestination) {
      if (data.destinationGeolocation) {
        checkoutData.destination_geolocation = data.destinationGeolocation;
        checkoutData.checkout_destination =
          data.destinationGeolocation.name ||
          data.destinationGeolocation.address ||
          'Custom Destination';
      } else {
        // If custom is selected but no geolocation, use checkout_destination as custom text
        checkoutData.checkout_destination = 'Custom Destination';
      }
      // Clear site ID when using custom location
      checkoutData.destination_site_id = undefined;
    } else if (data.destinationSiteId) {
      checkoutData.destination_site_id = data.destinationSiteId;
      // Clear geolocation when using site
      checkoutData.destination_geolocation = undefined;
      // Set checkout_destination to the selected site's name
      const selectedSite = resourceSites.find(
        (site: any) => site.id === data.destinationSiteId
      );
      if (selectedSite) {
        checkoutData.checkout_destination = selectedSite.name;
      }
    }
    onSave(checkoutData);
    setShowDemobilizedModal(false);
  };

  return (
    <CustomModal
      show={true}
      heading="Demobilization"
      onClose={() => setShowDemobilizedModal(false)}
      onConfirm={handleSubmit(onSubmit)}
      saveButton="Update Sign-off"
      modalStyle={{ '--bs-modal-width': '50vw' } as React.CSSProperties}
    >
      {exportAsForm && (
        <>
          <StylishNewButton
            className="button--secondary"
            onClick={() => {
              void exportAsForm({
                formname: 'ICS_221_CG',
                data: { resourceId: resource?.resource_in_incident_id },
                mapSketch: null,
              });
            }}
          >
            Preview ICS-221
          </StylishNewButton>
          <hr />
        </>
      )}
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {checkoffUnits.map((unit) => {
            const checkoff = checkoffs[unit.id];
            const canUncheck =
              !checkoff?.user_guid ||
              checkoff.user_guid === currentUser?.user_guid;

            return (
              <React.Fragment key={unit.id}>
                <div className="mb-3 p-3 border rounded">
                  <Row className="mb-2">
                    <Col md={12}>
                      <Form.Check
                        type="checkbox"
                        id={`checkoff-${unit.id}`}
                        label={<strong>{unit.label}</strong>}
                        checked={checkoff?.checked || false}
                        onChange={(e) =>
                          handleCheckoffChange(
                            unit.id,
                            'checked',
                            e.target.checked
                          )
                        }
                        disabled={
                          (checkoff?.checked && !canUncheck) ||
                          !hasApprovalAuthority
                        }
                      />
                      {checkoff?.checked && !canUncheck && (
                        <small className="text-muted">
                          (Signed by another user - cannot modify)
                        </small>
                      )}
                      {!hasApprovalAuthority && (
                        <ApprovalAuthorityWarning
                          message={`You don't have permission to approve ${unit.label.toLowerCase()} sign-offs. Please seek permission from a user with approval authority in the incident roster.`}
                          variant="warning"
                          className="mt-2"
                          inline
                        />
                      )}
                    </Col>
                  </Row>

                  {checkoff?.checked && (
                    <>
                      <Row className="mb-2">
                        <Col md={6}>
                          <label className="form-label small">Name</label>
                          <Form.Control
                            type="text"
                            value={checkoff.name || ''}
                            disabled
                            size="sm"
                          />
                        </Col>
                        <Col md={6}>
                          <label className="form-label small">Position</label>
                          <Form.Control
                            type="text"
                            value={checkoff.position || ''}
                            disabled
                            size="sm"
                          />
                        </Col>
                      </Row>
                      {checkoff.datetime && (
                        <Row className="mb-2">
                          <Col md={12}>
                            <small className="text-muted">
                              Signed on:{' '}
                              {new Date(checkoff.datetime).toLocaleString(
                                'en-US',
                                {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                }
                              )}
                            </small>
                          </Col>
                        </Row>
                      )}
                      <Row>
                        <Col md={12}>
                          <label className="form-label small">Remarks</label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Optional remarks..."
                            value={checkoff.remarks || ''}
                            onChange={(e) =>
                              handleCheckoffChange(
                                unit.id,
                                'remarks',
                                e.target.value
                              )
                            }
                            disabled={!canUncheck || !hasApprovalAuthority}
                            size="sm"
                          />
                        </Col>
                      </Row>
                    </>
                  )}

                  {unit.id === 'demobilization' && (
                    <div className="mt-2">
                      <small className="text-danger">
                        * Required for final checkout
                      </small>
                    </div>
                  )}
                </div>
              </React.Fragment>
            );
          })}
          {/* Remarks */}
          <Row className="mb-3">
            <Col md={12}>
              <FormTextAreaField fieldName="checkoutRemarks" title="Remarks" />
            </Col>
          </Row>

          <div>Travel Information:</div>

          {/* Estimated Departure */}
          <Row className="mb-3">
            <Col md={6}>
              <FormDateTimeField
                fieldName="estimatedDepartureDatetime"
                title="Estimated Departure Date/Time"
              />
            </Col>
            <Col md={6}>
              <FormDateTimeField
                fieldName="actualReleaseDatetime"
                title="Actual Release Date/Time"
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <FormSelectField
                fieldName="destinationSiteId"
                title="Demobilization Destination Site"
                options={resourceSiteOptions}
              />
            </Col>
            <Col md={6}>
              <FormDateTimeField
                fieldName="estimatedArrivalDatetime"
                title="Estimated Arrival Date/Time"
              />
            </Col>
          </Row>
          {/* Travel Method */}
          <Row className="mb-3">
            <Col md={6}>
              <FormTextField
                fieldName="checkoutTravelMethod"
                title="Travel Method"
              />
            </Col>
            <Col md={6}>
              <FormTextField
                fieldName="checkoutContactInfo"
                title="Contact Information While Traveling"
              />
            </Col>
          </Row>
          {/* Manifest */}
          <Row className="mb-3">
            <Col md={6}>
              <label className="form-label">Manifest</label>
              <div>
                <Form.Check
                  type="radio"
                  id="manifest-yes"
                  label="Yes"
                  value="yes"
                  {...form.register('manifestRequired')}
                  inline
                />
                <Form.Check
                  type="radio"
                  id="manifest-no"
                  label="No"
                  value="no"
                  {...form.register('manifestRequired')}
                  inline
                />
              </div>
            </Col>
            {manifestRequired === 'yes' && (
              <Col md={6}>
                <FormTextField fieldName="manifestNumber" title="Manifest #" />
              </Col>
            )}
          </Row>

          {/* Unit/Agency Notified */}
          <Row className="mb-3">
            <Col md={6}>
              <label className="form-label">Unit/Agency Notified</label>
              <div>
                <Form.Check
                  type="radio"
                  id="unit-agency-yes"
                  label="Yes"
                  value="yes"
                  {...form.register('unitAgencyNotifiedBool')}
                  inline
                />
                <Form.Check
                  type="radio"
                  id="unit-agency-no"
                  label="No"
                  value="no"
                  {...form.register('unitAgencyNotifiedBool')}
                  inline
                />
              </div>
            </Col>
            {unitAgencyNotifiedBool === 'yes' && (
              <Col md={6}>
                <FormTextField
                  fieldName="unitAgencyNotified"
                  title="Unit/Agency Name"
                />
              </Col>
            )}
          </Row>
          {/* Room Overnight */}
          <Row className="mb-3">
            <Col md={12}>
              <label className="form-label">Room Overnight</label>
              <div>
                <Form.Check
                  type="radio"
                  id="room-overnight-yes"
                  label="Yes"
                  value="yes"
                  {...form.register('roomOvernight')}
                  inline
                />
                <Form.Check
                  type="radio"
                  id="room-overnight-no"
                  label="No"
                  value="no"
                  {...form.register('roomOvernight')}
                  inline
                />
              </div>
            </Col>
          </Row>
        </form>
      </FormProvider>
    </CustomModal>
  );
};
